/**
 * @fileoverview User Management's "Add New User" form.
 * @description The finalized Add User dialog (First Name, Last Name, Email, Phone, Role, Department,
 * Reporting Manager), submitted as an invitation: POST /identity/invitations creates the user in Pending
 * Invitation and emails them a link to set their password. Role options are the sales roles the
 * backend says this admin may assign (GET /authorization/me/assignable-roles); Department options
 * are the departments the chosen role may be placed in (GET /users/eligible-departments) — a Sales
 * role is fixed to Sales, so the picker is preselected and locked — and its id is sent as
 * departmentId; Reporting Manager options are the backend's eligible managers for the chosen role
 * (GET /users/eligible-reporting-managers). A Sales Head reports to the Super Admin, shown as a
 * fixed value and not sent. An optional State (All India when left blank) is picked from the
 * `states` the page passes in, and sent as stateId only when chosen.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MANAGER_REQUIRED_ROLE_CODES } from '@/features/user-invitations/constants/invitation-roles.constants'
import {
  preselectedDepartmentId,
  useEligibleDepartments,
} from '@/features/user-invitations/hooks/useEligibleDepartments'
import { useSendInvitation } from '@/features/user-invitations/hooks/useSendInvitation'
import {
  createAddUserFormSchema,
  type AddUserFormValues,
} from '@/features/user-invitations/schemas/add-user-form.schema'
import {
  POSITION_LABELS,
  ROLE_LABELS,
  toSendInvitationRequest,
} from '@/features/user-invitations/utils/add-user-request.utils'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import FormField from '@/shared/components/molecules/FormField'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { SALES_ROLE_CODES } from '@/shared/constants/sales-role-codes.constants'
import { useAssignableRoles } from '@/shared/hooks/useAssignableRoles'
import { useEligibleReportingManagers } from '@/shared/hooks/useEligibleReportingManagers'
import { useLogout } from '@/shared/lib/auth/useLogout'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import type { SendInvitationResponse } from '@/features/user-invitations/types/user-invitations.types'
import type { FormEvent } from 'react'

interface AddUserModalProps {
  open: boolean
  onClose: () => void
  /** The states to offer (GET /tenants/prospect-metadata); the page loads them. */
  states?: { stateId: string; name: string }[]
  /** Called once the invitation is sent, with the new user's name and role label. */
  onCreated: (response: SendInvitationResponse, name: string, roleLabel: string) => void
}

const DEFAULT_VALUES: AddUserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  roleId: '',
  departmentId: '',
  reportsTo: '',
  stateId: '',
}

const NO_STATES: { stateId: string; name: string }[] = []

/** The role the form starts on, as in the finalized form. */
const DEFAULT_ROLE_CODE = 'SALES_EXECUTIVE'

/**
 * @description Appends a required-field marker to a label, matching the design mock's asterisks.
 * @param {string} label - The field's translated label.
 * @returns {string} The label with a trailing " *".
 */
function req(label: string): string {
  return `${label} *`
}

/**
 * @component
 * @param {AddUserModalProps} props - Whether the dialog is open and its callbacks.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called to dismiss the dialog without saving.
 * @param {{ stateId: string; name: string }[]} [props.states] - The states to offer.
 * @param {AddUserModalProps['onCreated']} props.onCreated - Called after the invitation is sent.
 */
function AddUserModal({ open, onClose, states = NO_STATES, onCreated }: AddUserModalProps) {
  const { t } = useTranslation()
  const sendInvitation = useSendInvitation()
  const logoutMutation = useLogout()
  const rolesQuery = useAssignableRoles(open)
  const salesRoles = useMemo(
    () => (rolesQuery.data ?? []).filter((role) => SALES_ROLE_CODES.has(role.roleCode)),
    [rolesQuery.data],
  )
  const roleById = useMemo(
    () => new Map(salesRoles.map((role) => [role.roleId, role])),
    [salesRoles],
  )
  const schema = useMemo(
    () =>
      createAddUserFormSchema((roleId) =>
        MANAGER_REQUIRED_ROLE_CODES.has(roleById.get(roleId)?.roleCode ?? ''),
      ),
    [roleById],
  )
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<AddUserFormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULT_VALUES })

  const roleId = useWatch({ control, name: 'roleId' })
  const role = roleById.get(roleId)
  const isSalesHead = role?.roleCode === 'SALES_HEAD'
  const managersQuery = useEligibleReportingManagers(roleId, open && !isSalesHead)
  const managers = managersQuery.data ?? []
  const departmentsQuery = useEligibleDepartments(roleId, open)
  const departments = departmentsQuery.data
  const departmentFixed = departments?.selectionMode === 'FIXED'

  // Start on Sales Executive once the assignable roles arrive, as the finalized form did.
  useEffect(() => {
    if (!roleId && salesRoles.length > 0) {
      const initial =
        salesRoles.find((candidate) => candidate.roleCode === DEFAULT_ROLE_CODE) ?? salesRoles[0]
      setValue('roleId', initial?.roleId ?? '')
    }
  }, [roleId, salesRoles, setValue])

  // Each role has its own departments: preselect the fixed or default one whenever they load.
  useEffect(() => {
    setValue('departmentId', preselectedDepartmentId(departments))
  }, [departments, setValue])

  /**
   * @description Dismisses the dialog, resetting the form and the mutation's error first, since
   * this component stays mounted between one open/close cycle and the next.
   */
  const handleClose = () => {
    reset(DEFAULT_VALUES)
    sendInvitation.reset()
    onClose()
  }

  /**
   * @description Sends the invitation, then closes and reports the new user to the page.
   * @param {AddUserFormValues} values - The validated form values.
   */
  const onValid = (values: AddUserFormValues) => {
    const chosenRole = roleById.get(values.roleId)
    if (!chosenRole) {
      return
    }
    sendInvitation.mutate(toSendInvitationRequest(values, chosenRole), {
      onSuccess: (response) => {
        handleClose()
        onCreated(
          response,
          `${values.firstName} ${values.lastName}`,
          ROLE_LABELS[chosenRole.roleCode] ?? chosenRole.name,
        )
      },
    })
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      data-testid="add-user-modal"
      open={open}
      onClose={handleClose}
      title={t('sales.userManagement.addUserForm.title')}
      subtitle={t('sales.userManagement.addUserForm.subtitle')}
      footer={
        <>
          <ModalFooterButton
            type="button"
            onClick={handleClose}
            data-testid="add-user-cancel-button"
          >
            {t('sales.pipeline.form.cancel')}
          </ModalFooterButton>
          <ModalFooterButton
            type="submit"
            variant="primary"
            form="add-user-form"
            data-testid="add-user-submit-button"
            isLoading={sendInvitation.isPending}
          >
            {t('sales.userManagement.addUserForm.submit')}
          </ModalFooterButton>
        </>
      }
    >
      <form
        id="add-user-form"
        data-testid="add-user-form"
        onSubmit={onSubmit}
        noValidate
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <FormField
          id="addUserFirstName"
          data-testid="add-user-first-name-input"
          size="md"
          placeholder={t('sales.userManagement.addUserForm.fields.firstNamePlaceholder')}
          label={req(t('sales.userManagement.addUserForm.fields.firstName'))}
          error={errors.firstName && t(errors.firstName.message ?? '')}
          {...register('firstName')}
        />
        <FormField
          id="addUserLastName"
          data-testid="add-user-last-name-input"
          size="md"
          placeholder={t('sales.userManagement.addUserForm.fields.lastNamePlaceholder')}
          label={req(t('sales.userManagement.addUserForm.fields.lastName'))}
          error={errors.lastName && t(errors.lastName.message ?? '')}
          {...register('lastName')}
        />
        <FormField
          id="addUserEmail"
          data-testid="add-user-email-input"
          size="md"
          type="email"
          placeholder={t('sales.userManagement.addUserForm.fields.emailPlaceholder')}
          label={req(t('sales.userManagement.addUserForm.fields.email'))}
          error={errors.email && t(errors.email.message ?? '')}
          {...register('email')}
        />
        <FormField
          id="addUserPhone"
          data-testid="add-user-phone-input"
          size="md"
          placeholder={t('sales.userManagement.addUserForm.fields.phonePlaceholder')}
          label={req(t('sales.userManagement.addUserForm.fields.phone'))}
          error={errors.phone && t(errors.phone.message ?? '')}
          {...MOBILE_NUMBER_INPUT_PROPS}
          {...register('phone')}
        />
        <SelectField
          id="addUserRole"
          data-testid="add-user-role-select"
          label={req(t('sales.userManagement.addUserForm.fields.role'))}
          options={salesRoles.map((option) => ({
            value: option.roleId,
            label: ROLE_LABELS[option.roleCode] ?? option.name,
          }))}
          error={errors.roleId && t(errors.roleId.message ?? '')}
          {...register('roleId')}
        />
        <div>
          <SelectField
            id="addUserDept"
            data-testid="add-user-department-select"
            label={req(t('sales.userManagement.addUserForm.fields.dept'))}
            options={[
              ...(departmentFixed
                ? []
                : [
                    {
                      value: '',
                      label: t('sales.userManagement.addUserForm.fields.reportsToPlaceholder'),
                    },
                  ]),
              ...(departments?.items ?? []).map((department) => ({
                value: department.departmentId,
                label: department.name,
              })),
            ]}
            // A Sales role's department is fixed to Sales.
            disabled={departmentFixed}
            error={errors.departmentId && t(errors.departmentId.message ?? '')}
            {...register('departmentId')}
          />
          {departmentsQuery.isError && (
            <p
              role="alert"
              className="mt-1.5 text-[12px] text-danger"
              data-testid="add-user-department-load-error"
            >
              {departmentsQuery.error.message}
            </p>
          )}
        </div>
        {isSalesHead ? (
          <SelectField
            id="addUserReportsTo"
            data-testid="add-user-reports-to-select"
            label={t('sales.userManagement.addUserForm.fields.reportsTo')}
            options={[{ value: 'Super Admin', label: t('sales.userManagement.superAdmin') }]}
            value="Super Admin"
            onChange={() => {}}
          />
        ) : (
          <div>
            <SelectField
              id="addUserReportsTo"
              data-testid="add-user-reports-to-select"
              label={req(t('sales.userManagement.addUserForm.fields.reportsTo'))}
              options={[
                {
                  value: '',
                  label: t('sales.userManagement.addUserForm.fields.reportsToPlaceholder'),
                },
                ...managers.map((manager) => ({
                  value: manager.userId,
                  label: `${manager.displayName} · ${POSITION_LABELS[manager.positionCode] ?? manager.positionCode}`,
                })),
              ]}
              error={errors.reportsTo && t(errors.reportsTo.message ?? '')}
              {...register('reportsTo')}
            />
            {managersQuery.isSuccess && managers.length === 0 && (
              <p
                className="mt-1.5 text-[12px] text-slate"
                data-testid="add-user-no-managers-message"
              >
                {t('sales.userManagement.addUserForm.fields.reportsToNoneAvailable')}
              </p>
            )}
          </div>
        )}

        <SelectField
          id="addUserState"
          data-testid="add-user-state-select"
          label={t('sales.userManagement.addUserForm.fields.state')}
          options={[
            { value: '', label: t('sales.userManagement.addUserForm.fields.allIndia') },
            ...states.map((state) => ({ value: state.stateId, label: state.name })),
          ]}
          {...register('stateId')}
        />

        {sendInvitation.isError && (
          <div
            role="alert"
            className="col-span-full text-[13px] text-danger"
            data-testid="add-user-error-message"
          >
            <p>{sendInvitation.error.message}</p>
            {sendInvitation.error.code === ERROR_CODES.STEP_UP_MFA_REQUIRED && (
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                data-testid="add-user-sign-in-again-button"
                className="mt-1 cursor-pointer font-semibold text-primary underline"
              >
                {t('userInvitations.signInAgain')}
              </button>
            )}
          </div>
        )}
      </form>
    </Modal>
  )
}

export default AddUserModal
